import React, { useEffect, useRef, useState } from "react";
import "./TradingTerminal.css";
import ExchangeNavBar from "../widgets/ExchangeNavBar";
import TradingChartBox from "../partials/unifiedLayout/TradingChartBox";
import TradeBox from "../partials/unifiedLayout/TradeBox";
import WatchlistPanel from "../partials/unifiedLayout/WatchlistPanel";
import socketIOClient from "socket.io-client";
import { updatePrice } from "../../redux/marketSlice";
import { imageUrl, socketUrl } from "../constant/BaseUrl";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrder,
  cancelOrder,
  fillOrder,
  setClosedOrders,
} from "../../redux/ordersSlice";
import {
  addPosition,
  closePosition,
  setPositions,
  updatePosition,
} from "../../redux/positionsSlice";
import {
  useCloseTradesPnlQuery,
  useGetBalanceQuery,
  useGetClosedPositionsQuery,
  useGetUserPositionsQuery,
} from "../../redux/exchangeUniApi";

import { useTranslation } from "react-i18next";
import {
  updateOpenPnl,
  setAccountBalance,
  updateClosedPnl,
  addClosedPnl,
} from "../../redux/accountUniSlice";
import toast from "react-hot-toast";
import { showOrderNotification } from "../partials/unifiedLayout/showOrderNotification";
import { useGetSelectedLeverageQuery } from "../../redux/forexApi";
import { addNotification } from "../../redux/notificationsSlice";
import { useNavigate } from "react-router-dom";

const UnifiedLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [theme, setTheme] = useState("dark");
  const [userLeverage, setUserLeverage] = useState(1);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const userData = useSelector((state) => state?.user?.userData);
  const [mobileActiveTab, setMobileActiveTab] = useState("Markets");
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const [isOrderBoxShow, setIsOrderBoxShow] = useState(true);
  // const [screenSize, setScreenSize] = useState("tv_chart_container2-lower");
  const [screenSize, setScreenSize] = useState("tv_chart_container2");
  const [existingOrder, setExistingOrder] = useState(null);
  const positions = useSelector((state) => state.positions.data);
  const { data: accountData, refetch: refetchBalance } = useGetBalanceQuery(
    userData?.id
  );
  const { data: selectedLeverages } = useGetSelectedLeverageQuery();
  const { data: closedPnl, refetch: refetchClosePnl } = useCloseTradesPnlQuery(
    userData?.id
  );
  const { data: closedPositions, refetch: refetchClosePositions } =
    useGetClosedPositionsQuery(userData?.id);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePositionClosed = () => {
    setShouldCloseModal(true); // Trigger modal close in TradeBox
  };

  const handleModalCloseComplete = () => {
    setShouldCloseModal(false); // Reset trigger
  };

  useEffect(() => {
    if (existingOrder) {
      setMobileActiveTab("BuySell");
    }
  }, [existingOrder]);

  useEffect(() => {
    if (selectedLeverages) {
      if (selectedLeverages) {
        const cryptoLeverage = selectedLeverages.amount || 1;
        setUserLeverage(cryptoLeverage);
      } else {
        setUserLeverage(1);
      }
    }
  }, [selectedLeverages]);

  useEffect(() => {
    if (closedPnl !== undefined) {
      dispatch(addClosedPnl(closedPnl));
    }
  }, [closedPnl]);

  useEffect(() => {
    if (closedPositions) {
      dispatch(setClosedOrders(closedPositions));
    }
  }, [closedPositions, dispatch]);

  const { data: openPositions, refetch } = useGetUserPositionsQuery(
    userData?.id
  );
  const livePrices = useSelector((state) => state.market.prices);

  useEffect(() => {
    if (accountData) {
      dispatch(setAccountBalance(accountData)); // balance, usedMargin, available
    }
  }, [accountData, dispatch]);

  useEffect(() => {
    if (!userData?.id || !userData?.refred_by_id) return;

    const socket = socketIOClient(socketUrl, {
      transports: ["websocket"],
      query: {
        token: localStorage.getItem("jwtToken"),
      },
      reconnection: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("🟢 Connected to WebSocket");

      socket.emit("subscribe_watchlist", {
        userId: userData.id,
        brokerId: userData.refred_by_id,
      });

      socket.emit("subscribe_user", userData.id);
    });

    socket.on("marketData", (data) => {
      // console.log(data, "price");
      dispatch(updatePrice(data));
      // handleMarketUpdate(data);
    });

    socket.on("user_event", (data) => {
      // console.log(data, "from user_events");

      if (data.type === "notifications") {
        dispatch(addNotification(data));
      }

      if (data.type === "order_placed") {
        dispatch(addOrder({ type: "order_placed", payload: data }));
        showOrderNotification(data);
      }
      if (data.type === "order_filled") {
        // dispatch(addOrder({ type: "order_filled", payload: data }));
        dispatch(fillOrder({ order_id: data.order_id }));
        dispatch(
          addPosition({
            id: data.position_id,
            symbol: data.symbol,
            entry_price: data.entry_price,
            quantity: data.quantity,
            side: data.side === "BUY" ? "long" : "short",
            status: "open",
            stop_loss: data.stop_loss,
            take_profit: data.take_profit,
            created_at: new Date().toISOString(),
            segment: data.segment,
            fee: data.fee,
            contractSize: data.contractSize,
          })
        );
        showOrderNotification(data);
      }

      if (data.type === "position_closed") {
        dispatch(fillOrder({ order_id: data.order_id }));
        dispatch(addOrder({ type: "position_closed", payload: data }));
        dispatch(closePosition(data.position_id));
        showOrderNotification(data);
        dispatch(updateClosedPnl(parseFloat(data.pnl)));
      }

      if (data.type === "order_cancelled") {
        dispatch(addOrder({ type: "order_cancelled", payload: data }));
        dispatch(cancelOrder(data.order_id));
        showOrderNotification(data);
      }

      if (data.type === "positions_updated") {
        dispatch(updatePosition(data));
        showOrderNotification(data);
      }

      if (data.type === "insufficient_balance") {
        toast.dismiss();
        toast.error(data.message);
      }
      refetchBalance();
    });

    socket.on("disconnect", () => {
      console.log("🔴 Disconnected from WebSocket");
    });

    return () => socket.disconnect();
  }, [userData?.id, userData?.refred_by_id, dispatch]);

  useEffect(() => {
    if (openPositions?.length) {
      dispatch(setPositions(openPositions));
    }
  }, [openPositions, dispatch]);

  function calculatePnlFrontend(pos, livePriceMap) {
    const entry = parseFloat(pos.entry_price);
    const qty = parseFloat(pos.quantity);
    const lev = parseFloat(pos.leverage || 1);
    const symbol = pos.symbol?.toUpperCase();
    const segment = pos.segment || "crypto";
    const contractSize = pos.contractSize || 1;
    const side = pos.side?.toLowerCase();
    const lots = qty;

    const livePrice = livePriceMap[symbol];
    if (!livePrice || (!livePrice.bid && !livePrice.ask)) return 0;

    const currentPrice = side === "long" ? livePrice.bid : livePrice.ask;
    if (!currentPrice || currentPrice === 0) return 0;

    const direction = side === "long" ? 1 : -1;

    let pnlInUSD = 0;

    if (segment === "forex") {
      const baseCurrency = symbol.slice(0, 3).toUpperCase();
      const quoteCurrency = symbol.slice(3, 6).toUpperCase();

      if (baseCurrency === "USD") {
        // ✅ USD_BASE: treat result as already in USD
        const pnlInQuote =
          (direction * (currentPrice - entry) * contractSize * lots) /
          currentPrice;
        pnlInUSD = pnlInQuote;
      } else if (quoteCurrency === "USD") {
        // ✅ USD_QUOTE: already in USD
        pnlInUSD = direction * (currentPrice - entry) * contractSize * lots;
      } else {
        // ✅ NON_USD → convert from quote currency to USD
        const pnlInQuote =
          direction * (currentPrice - entry) * contractSize * lots;

        let fxRate = 1;
        const direct = `${quoteCurrency}USD`;
        const inverse = `USD${quoteCurrency}`;

        if (livePriceMap[direct]?.bid) {
          fxRate = livePriceMap[direct].bid;
        } else if (livePriceMap[inverse]?.ask) {
          fxRate = 1 / livePriceMap[inverse].ask;
        } else {
          return 0; // can't convert
        }

        pnlInUSD = pnlInQuote * fxRate;
      }
    } else {
      // ✅ Crypto / Equity
      pnlInUSD = direction * (currentPrice - entry) * contractSize * lots;
    }

    const fee = parseFloat(pos.fee || 0);
    return pnlInUSD - fee;
  }

  useEffect(() => {
    let totalOpenPnl = 0;

    for (let pos of positions) {
      const pnl = calculatePnlFrontend(pos, livePrices);
      totalOpenPnl += pnl;
    }

    dispatch(updateOpenPnl(parseFloat(totalOpenPnl.toFixed(2))));
  }, [positions, livePrices]);

  const handleEditPositions = (pos) => {
    setExistingOrder(pos);
    setIsOrderBoxShow(true);
  };

  const changeChartTheme = (theme) => {
    if (theme === "defaultTheme" || theme === "darkTheme") {
      setTheme("dark");
    } else {
      setTheme("light");
    }
  };

  const renderTab = () => {
    switch (mobileActiveTab) {
      case "Markets":
        return <WatchlistPanel socket={socketRef.current} />;
      case "Charts":
        return (
          <TradingChartBox
            isOrderBoxShow={isOrderBoxShow}
            setIsOrderBoxShow={setIsOrderBoxShow}
            getExistingOrder={handleEditPositions}
            manageScreenSize={screenSize}
            setScreenSize={setScreenSize}
            theme={theme}
            leverage={userLeverage}
            isOrderBoxOn={false}
            isChartShow={true}
            onPositionClosed={handlePositionClosed}
          />
        );
      case "BuySell":
        return (
          <TradeBox
            isOrderBoxShow={isOrderBoxShow}
            setIsOrderBoxShow={setIsOrderBoxShow}
            existingOrder={existingOrder}
            getExistingOrder={setExistingOrder}
            leverage={userLeverage}
            shouldCloseModal={shouldCloseModal}
            onModalClosed={handleModalCloseComplete}
          />
        );
      case "Trades":
        return (
          <TradingChartBox
            isOrderBoxShow={isOrderBoxShow}
            setIsOrderBoxShow={setIsOrderBoxShow}
            getExistingOrder={handleEditPositions}
            manageScreenSize={screenSize}
            setScreenSize={setScreenSize}
            theme={theme}
            leverage={userLeverage}
            isOrderBoxOn={true}
            isChartShow={false}
            onPositionClosed={handlePositionClosed}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <ExchangeNavBar changeChartTheme={changeChartTheme} unified={true} />

      <div className="new_dashbord ">
        {isMobile ? (
          <div className="mobile-view">
            <div className="tab-content">{renderTab()}</div>
            <div className="bottom-tab-bar bg2">
              <button
                className={
                  mobileActiveTab === "Markets" ? `tab-btn active` : `tab-btn `
                }
                onClick={() => {
                  setMobileActiveTab("Markets");
                }}
              >
                <i className="d-block bi bi-list-ul"></i> Markets{" "}
              </button>
              <button
                className={
                  mobileActiveTab === "Charts" ? `tab-btn active` : `tab-btn `
                }
                onClick={() => {
                  setMobileActiveTab("Charts");
                }}
              >
                <i className="d-block bi bi-graph-up-arrow"></i> Charts{" "}
              </button>
              <button
                className={`tab-btn`}
                onClick={() => setMobileActiveTab("BuySell")}
              >
                {" "}
                <span className="responsive_item_gmx">
                  <img
                    className="img-fluid"
                    src="/img/logo-icon.png"
                    alt="logo coin"
                  />
                </span>
                BuySell{" "}
              </button>
              <button
                className={
                  mobileActiveTab === "Trades" ? `tab-btn active` : `tab-btn `
                }
                onClick={() => setMobileActiveTab("Trades")}
              >
                <i className="d-block bi bi-clock-history"></i> Trades{" "}
              </button>
              <button
                className={`tab-btn `}
                onClick={() => navigate("/dashboard")}
              >
                <i className="d-block bi bi-person-gear"></i> Dashboard{" "}
              </button>
            </div>
          </div>
        ) : (
          <div className="dash-grid">
            <div className={isOrderBoxShow ? "dash-grid-l" : "dash-grid-l-f"}>
              <TradingChartBox
                isOrderBoxShow={isOrderBoxShow}
                setIsOrderBoxShow={setIsOrderBoxShow}
                getExistingOrder={handleEditPositions}
                manageScreenSize={screenSize}
                setScreenSize={setScreenSize}
                theme={theme}
                leverage={userLeverage}
                isOrderBoxOn={true}
                isChartShow={true}
                onPositionClosed={handlePositionClosed}
              />
            </div>
            <div className={isOrderBoxShow ? "dash-grid-r" : "dash-grid-r-f"}>
              <WatchlistPanel socket={socketRef.current} />
              <TradeBox
                isOrderBoxShow={isOrderBoxShow}
                setIsOrderBoxShow={setIsOrderBoxShow}
                existingOrder={existingOrder}
                getExistingOrder={setExistingOrder}
                leverage={userLeverage}
                shouldCloseModal={shouldCloseModal}
                onModalClosed={handleModalCloseComplete}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedLayout;
